import React, { useMemo, useState } from "react";
import MembershipActionsMenu from "./MembershipActionsMenu";
import ActionsMenu from "../../shared/components/ActionsMenu";
import StatusBadge, { VARIANT_STYLES } from "../../../shared/StatusBadge";
import TeamSearchBar from "../../TeamManager/components/TeamSearchBar";
import PlusIcon from "../../shared/Icons/PlusIcon";
import SortIcon from "../../shared/Icons/SortIcon";
import SearchIcon from "../../shared/Icons/SearchIcon";
import MembershipIcon from "../../shared/Icons/MembershipIcon";
import CopyIcon from "../../shared/Icons/CopyIcon";
import GlobeIcon from "../../shared/Icons/GlobeIcon";
import { toastSuccess, toastError } from "../../../shared/toastHelper";
import "../../TeamManager/styles/teamManagerStyles.css";
import { copyText } from '@opendate-app/setlist';
import { ticketWidgetTag } from "../../shared/CheckoutEmbedModal/embedSnippets";

const VENUE_BADGE_COLORS = ['blue', 'red', 'yellow', 'green', 'purple', 'orange'];

const getColorForIndex = (index) => VENUE_BADGE_COLORS[index % VENUE_BADGE_COLORS.length];

const STATUS_LABELS = { active: 'Published', draft: 'Draft', archived: 'Archived' };

const MembershipCard = ({ membershipProducts, team, publicMembershipsUrl, publicMembershipsWidgetUrl, onDelete, onStatusChange }) => {
  const [sortBy, setSortBy] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');
  const [searchTerm, setSearchTerm] = useState('');

  const filteredAndSortedProducts = useMemo(() => {
    let filtered = membershipProducts;

    if (searchTerm.trim()) {
      const term = searchTerm.trim().toLowerCase();
      filtered = membershipProducts.filter((mp) => {
        const name = (mp.name || '').toLowerCase();
        const description = (mp.description || '').toLowerCase();
        const venueNames = (mp.venue_ownerships || [])
          .map((vo) => (vo.name || '').toLowerCase())
          .join(' ');
        return name.includes(term) || description.includes(term) || venueNames.includes(term);
      });
    }

    return [...filtered].sort((a, b) => {
      if (sortBy === 'name') {
        const nameA = (a.name || '').toLowerCase();
        const nameB = (b.name || '').toLowerCase();
        return sortDirection === 'asc'
          ? nameA.localeCompare(nameB)
          : nameB.localeCompare(nameA);
      }
      if (sortBy === 'price') {
        const priceA = a.price || 0;
        const priceB = b.price || 0;
        return sortDirection === 'asc' ? priceA - priceB : priceB - priceA;
      }
      if (sortBy === 'venues') {
        const countA = a.venue_ownerships ? a.venue_ownerships.length : 0;
        const countB = b.venue_ownerships ? b.venue_ownerships.length : 0;
        return sortDirection === 'asc' ? countA - countB : countB - countA;
      }
      return 0;
    });
  }, [membershipProducts, searchTerm, sortBy, sortDirection]);

  const handleSortClick = (columnKey) => {
    if (sortBy === columnKey) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(columnKey);
      setSortDirection('asc');
    }
  };

  const handleCreate = () => {
    window.location.href = `/teams/${team.id}/membership_products/new`;
  };

  const handleCopyLink = async () => {
    try {
      const ok = await copyText(publicMembershipsUrl);
      if (!ok) throw new Error('copy failed');
      toastSuccess('Public page link copied to clipboard.');
    } catch {
      toastError('Could not copy link. Please copy it manually.');
    }
  };

  const handleCopyEmbedCode = async () => {
    try {
      const embedCode = ticketWidgetTag(publicMembershipsWidgetUrl, `od-memberships-${team.id}-iframe`, "Memberships");
      const ok = await copyText(embedCode);
      if (!ok) throw new Error('copy failed');
      toastSuccess('Embed code copied to clipboard.');
    } catch {
      toastError('Could not copy embed code. Please copy it manually.');
    }
  };

  const handleViewLink = () => {
    window.open(publicMembershipsUrl, '_blank', 'noopener');
  };

  const publicPageActions = [
    { label: "Copy Link", icon: <CopyIcon fill="currentColor" width="16" height="16" />, onClick: handleCopyLink },
    { label: "Copy Embed Code", icon: "fal fa-code", onClick: handleCopyEmbedCode },
    { label: "View Link", icon: <GlobeIcon fill="currentColor" width="16" height="16" />, onClick: handleViewLink },
  ];

  return (
    <div className="team-member-card">
      <div className="team-member-card-header">
        <div className="team-member-card-header-left">
          <div className="team-member-card-title-row">
            <h2 className="team-member-card-title">Memberships</h2>
            <span className="team-member-count-badge">
              {membershipProducts.length} {membershipProducts.length === 1 ? 'plan' : 'plans'}
            </span>
          </div>
          <p className="team-member-card-subtitle">
            Create and manage membership plans for your patrons and fans.
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {publicMembershipsUrl && (
            <ActionsMenu
              actions={publicPageActions}
              buttonClassNames={["team-actions-btn-square"]}
            />
          )}
          <button type="button" className="team-btn-primary" onClick={handleCreate}>
            <PlusIcon fill="currentColor" width="16" height="16" />
            <span>Create Membership</span>
          </button>
        </div>
      </div>

      {membershipProducts.length === 0 ? (
        <div className="team-empty-state">
          <div className="team-empty-state-icon">
            <MembershipIcon width="24" height="24" />
          </div>
          <div className="team-empty-state-title">No memberships yet</div>
          <div className="team-empty-state-text">
            Create a membership plan to get started
          </div>
        </div>
      ) : (
        <>
        <div className="team-search-filters-row">
          <div className="team-search-filters-left">
            <TeamSearchBar
              searchTerm={searchTerm}
              onSearchChange={setSearchTerm}
              placeholder="Search memberships..."
            />
          </div>
        </div>
        {filteredAndSortedProducts.length === 0 ? (
          <div className="team-empty-state">
            <div className="team-empty-state-icon">
              <SearchIcon width="24" height="24" />
            </div>
            <div className="team-empty-state-title">No memberships found</div>
            <div className="team-empty-state-text">
              Try adjusting your search
            </div>
          </div>
        ) : (
        <div className="team-table-wrapper team-table-wrapper--membership">
          <table className="team-table">
            <colgroup>
              <col style={{ width: '32%' }} />
              <col style={{ width: '26%' }} />
              <col style={{ width: '14%' }} />
              <col style={{ width: '14%' }} />
              <col style={{ width: '12%' }} />
              <col style={{ width: '48px' }} />
            </colgroup>
            <thead>
              <tr>
                <th style={{ paddingLeft: '24px' }}>
                  <button
                    type="button"
                    className="team-sort-header"
                    onClick={() => handleSortClick('name')}
                  >
                    <span>Membership</span>
                    <span style={{ marginLeft: '6px', display: 'inline-flex', color: 'var(--od-faint, #a0a4ab)' }}>
                      <SortIcon active={sortBy === 'name'} direction={sortDirection} width="12" height="12" />
                    </span>
                  </button>
                </th>
                <th>
                  <button
                    type="button"
                    className="team-sort-header"
                    onClick={() => handleSortClick('venues')}
                  >
                    <span>Venues</span>
                    <span style={{ marginLeft: '6px', display: 'inline-flex', color: 'var(--od-faint, #a0a4ab)' }}>
                      <SortIcon active={sortBy === 'venues'} direction={sortDirection} width="12" height="12" />
                    </span>
                  </button>
                </th>
                <th>
                  <button
                    type="button"
                    className="team-sort-header"
                    onClick={() => handleSortClick('price')}
                  >
                    <span>Price</span>
                    <span style={{ marginLeft: '6px', display: 'inline-flex', color: 'var(--od-faint, #a0a4ab)' }}>
                      <SortIcon active={sortBy === 'price'} direction={sortDirection} width="12" height="12" />
                    </span>
                  </button>
                </th>
                <th>Visibility</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filteredAndSortedProducts.map((mp, index) => {
                const venues = mp.venue_ownerships || [];

                return (
                  <tr key={mp.id}>
                    <td style={{ paddingLeft: '24px' }} data-label="Membership">
                      <a
                        href={`/teams/${team.id}/membership_products/${mp.id}`}
                        className="team-member-info"
                        style={{ textDecoration: 'none', color: 'inherit' }}
                      >
                        <div
                          className="team-avatar"
                          style={{
                            borderRadius: '8px',
                            overflow: 'hidden',
                            backgroundColor: 'var(--sl-bg-tertiary)',
                          }}
                        >
                          {mp.image_thumb_url ? (
                            <img
                              src={mp.image_thumb_url}
                              alt={mp.name}
                              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            />
                          ) : (
                            <span style={{ color: 'var(--sl-text-placeholder)', display: 'inline-flex' }}>
                              <MembershipIcon fill="currentColor" width="18" height="18" />
                            </span>
                          )}
                        </div>
                        <div className="team-member-details">
                          <span className="team-member-name">{mp.name}</span>
                          {mp.description && (
                            <span className="team-member-email">
                              {mp.description.length > 60
                                ? mp.description.substring(0, 60) + '...'
                                : mp.description}
                            </span>
                          )}
                        </div>
                      </a>
                    </td>
                    <td data-label="Venues">
                      <div className="team-venue-badges">
                        {venues.slice(0, 3).map((vo, voIndex) => (
                          <span key={vo.id} className="team-venue-badge">
                            <span className={`team-venue-dot team-venue-dot-${getColorForIndex(voIndex)}`}></span>
                            <span>{vo.name}</span>
                          </span>
                        ))}
                        {venues.length > 3 && (
                          <span className="team-venue-more-badge">
                            +{venues.length - 3}
                          </span>
                        )}
                      </div>
                    </td>
                    <td data-label="Price">
                      <span className="team-role-text">
                        {mp.price_to_currency || 'N/A'}
                      </span>
                    </td>
                    <td data-label="Visibility">
                      <span
                        style={{
                          fontSize: 12,
                          fontWeight: 500,
                          padding: '2px 10px',
                          borderRadius: 16,
                          whiteSpace: 'nowrap',
                          ...VARIANT_STYLES[mp.listed_publicly !== false ? 'info' : 'neutral'],
                        }}
                      >
                        {mp.listed_publicly !== false ? 'Public' : 'Private'}
                      </span>
                    </td>
                    <td data-label="Status">
                      <StatusBadge status={mp.status} label={STATUS_LABELS[mp.status] || mp.status} />
                    </td>
                    <td>
                      <MembershipActionsMenu
                        membershipProduct={mp}
                        team={team}
                        onDelete={onDelete}
                        onStatusChange={onStatusChange}
                        openUpwards={index >= filteredAndSortedProducts.length - 2}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        )}
        </>
      )}
    </div>
  );
};

export default MembershipCard;
